import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Mail, Key, RefreshCw, AlertCircle, Loader2, BarChart3, Shield, Zap, ArrowRight } from 'lucide-react';
import { authService } from '../services/authService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Login Page
 * Allows users to login with IronSource credentials
 */
const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Prefill form with dev credentials from env in development mode
  const isDevelopment = import.meta.env.VITE_APP_ENV === 'development' || import.meta.env.DEV;
  const devEmail = import.meta.env.VITE_DEV_EMAIL || '';
  const devSecretKey = import.meta.env.VITE_DEV_SECRET_KEY || '';
  const devRefreshToken = import.meta.env.VITE_DEV_REFRESH_TOKEN || '';
  
  const [formData, setFormData] = useState({
    email: isDevelopment ? devEmail : '',
    secret_key: isDevelopment ? devSecretKey : '',
    refresh_token: isDevelopment ? devRefreshToken : '',
  });

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login(formData.email, formData.secret_key, formData.refresh_token);

      if (response.data?.token) {
        // Redirect to dashboard on successful login
        navigate('/dashboard');
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError(null);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (formData.email && formData.secret_key && formData.refresh_token && !loading) {
        handleSubmit(e);
      }
    }
  };

  return (
    <div className="flex flex-1 w-full min-h-0 overflow-x-hidden">
      {/* Left Side - Branding/Info - fills height between header and footer */}
      <div className="hidden lg:flex lg:w-1/2 min-h-full bg-gradient-to-br from-primary/10 via-background to-primary/5 p-8 lg:p-12 flex-col justify-between">
        <div>
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-6">
              <Zap className="w-4 h-4" />
              <span>Powered by NAndroidEx</span>
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Welcome Back
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Sign in to access your comprehensive IronSource analytics dashboard. Track revenue, monitor performance, and make data-driven decisions.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Real-Time Analytics</h3>
                  <p className="text-sm text-muted-foreground">Monitor your app performance with live data and insights</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Secure Access</h3>
                  <p className="text-sm text-muted-foreground">Enterprise-grade security for your data and credentials</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary inline-flex items-center gap-1 transition-colors">
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Right Side - Login Form - fills remaining height */}
      <div className="flex-1 min-h-full flex items-center justify-center p-4 sm:p-6 lg:p-8 xl:p-12 bg-background overflow-y-auto min-h-0">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6 text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Welcome Back</h1>
            <p className="text-muted-foreground text-sm sm:text-base">Sign in to your account</p>
          </div>

          <Card className="border-2">
            <CardHeader className="space-y-1">
              <div className="flex justify-center mb-4 lg:hidden">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <LogIn className="w-8 h-8 text-primary" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-center lg:text-left">Sign In</CardTitle>
              <CardDescription className="text-center lg:text-left">
                Enter your IronSource credentials to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="space-y-4"
                onKeyDown={onKeyDown}
                role="form"
                aria-label="IronSource credentials"
              >
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="pl-9"
                      required
                      disabled={loading}
                      autoComplete="off"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Find your email at{' '}
                    <a
                      href="https://platform.ironsrc.com/partners/account/generalInfo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      IronSource General Info
                    </a>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="secret_key">Secret Key</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="secret_key"
                      type="password"
                      placeholder="Enter your IronSource secret key"
                      value={formData.secret_key}
                      onChange={(e) => handleChange('secret_key', e.target.value)}
                      className="pl-9"
                      required
                      disabled={loading}
                      autoComplete="off"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Find your secret key at{' '}
                    <a
                      href="https://platform.ironsrc.com/partners/account/apiDetails"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      IronSource API Details
                    </a>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="refresh_token">Refresh Token</Label>
                  <div className="relative">
                    <RefreshCw className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="refresh_token"
                      type="password"
                      placeholder="Enter your IronSource refresh token"
                      value={formData.refresh_token}
                      onChange={(e) => handleChange('refresh_token', e.target.value)}
                      className="pl-9"
                      required
                      disabled={loading}
                      autoComplete="off"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Find your refresh token at{' '}
                    <a
                      href="https://platform.ironsrc.com/partners/account/apiDetails"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      IronSource API Details
                    </a>
                  </p>
                </div>

                <Button
                  type="button"
                  className="w-full"
                  onClick={handleSubmit}
                  disabled={loading || !formData.email || !formData.secret_key || !formData.refresh_token}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>
              </div>

              <div className="mt-6 text-center text-sm text-muted-foreground">
                <p>
                  Don't have an IronSource account?{' '}
                  <a
                    href="https://www.ironsrc.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Sign up here
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
